import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import { Project } from '../../../../../models/project.module';
import { ToastNotificationService } from '../../../../../services/toast-notification.service';
import { findFirstNonEmptyString } from '../../../../../utils';
import { NewProjectService } from '../../../../../services/newProject.service';
import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStructure } from '../../../../../models/general.models';
import { User } from '../../../../../models/user.model';

@Component({
  selector: 'frmg-create-modal',
  templateUrl: './create-project-modal.component.html',
  styleUrls: ['./create-project-modal.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatError,
    CdkTextareaAutosize,
    MatButton,
    MatDialogClose,
    NgClass
  ]
})
export class CreateProjectModalComponent implements OnInit {
  public newProjectGroup: FormGroup;
  public currentUser: User;
  public isLoading = false;
  public errors: ErrorStructure[] = [];
  // eslint-disable-next-line no-control-regex
  private forbiddenPattern = /^[^<>:"/\\|?*\x00]*$/;

  @ViewChild('autosize') autosize: CdkTextareaAutosize;

  constructor(
    private dialogRef: MatDialogRef<CreateProjectModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { parent: Project; project?: Project },
    private fb: FormBuilder,
    private toastNotificationService: ToastNotificationService,
    private newProjectService: NewProjectService
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('user')) as User;
    this.newProjectGroup = this.fb.group({
      name: [this.data.project?.name || '', [Validators.required, Validators.maxLength(255), Validators.pattern(this.forbiddenPattern)]],
      description: [this.data.project?.description || '', [Validators.maxLength(200)]]
    });
    this.newProjectGroup.valueChanges.subscribe(_ => {
      this.newProjectGroup.clearValidators();
      this.errors = [];
    });
  }

  ngOnInit(): void {
  }

  public closeDialog(result?: any): void {
    this.dialogRef.close(result);
  }

  public addProject(): void {
    this.errors = [];
    this.isLoading = true;
    const groupValue = this.newProjectGroup.value;

    if (this.data.project) {
      this.newProjectService
        .changeProject(this.data.project.id, {
          name: groupValue.name,
          description: groupValue.description
        })
        .subscribe(
          (update) => {
            this.isLoading = false;
            this.toastNotificationService.showNotification({
              type: 'success',
              message: 'Project updated Successfully'
            });
            this.closeDialog(update);
          },
          (error: HttpErrorResponse) => {
            const errorMsg = 'An error occurred while creating the project.';
            switch (error.status) {
              case 422:
                this.errors = error.error.detail;
                break;
              default:
                this.toastNotificationService.showNotification({
                  type: 'error',
                  message: findFirstNonEmptyString(error.error.detail[0].msg) || errorMsg
                });
            }
            this.isLoading = false;
          }
        );
    } else {
      const projectData = {
        name: groupValue.name,
        description: groupValue.description,
        parent_id: this.data.parent?.id,
        project_metadata: {
          created_by: this.currentUser.email,
          created_at: new Date().toISOString(),
          parent_folder: this.data.parent?.name,
          root_project: this.data.parent?.id
        }
      };
      if (this.data.parent?.id) {
        projectData.parent_id = this.data.parent?.id;
      }

      this.newProjectService.createProject(projectData).subscribe(
        (newProject) => {
          this.isLoading = false;
          this.toastNotificationService.showNotification({
            type: 'success',
            message: 'Project Created Successfully'
          });
          this.closeDialog(newProject);
        },
        (error: HttpErrorResponse) => {
          const errorMsg = 'An error occurred while creating the project.';
          switch (error.status) {
            case 422:
              this.errors = error.error.detail;
              break;
            default:
              this.toastNotificationService.showNotification({
                type: 'error',
                message: findFirstNonEmptyString(error.error.detail[0].msg) || errorMsg
              });
          }
          this.isLoading = false;
        }
      );
    }
  }

  public fieldError(key: string) {
    if (this.errors.length) {
      const error = this.errors.find(error => error.loc.includes(key));
      if (error) {
        this.newProjectGroup.get(key).setErrors({ incorrect: true });
        return error.msg;
      } else {
        return '';
      }
    } else {
      return '';
    }
  }

  public descLength() {
    return this.newProjectGroup.get('description').value.length;
  }
}
